import React, { useState } from "react";
import * as HeroIcons from '@heroicons/react/24/outline';
import { ColumnDef } from "@tanstack/react-table";
import { DataTableColumnHeader } from "components/ui/data-table-column-header";
import { createStandardHeader } from "components/ui/standard-column-header";
import { titleCase, upperCase } from "lib/utils";
import { CnDocumentModal } from "../in-transit/CnDocumentModal";

interface DMVShipment {
  cn_number: string;
  cn_date: string;
  branch_name: string;
  consignor: string;
  consignee: string;
  origin: string;
  destination: string;
  cn_distance_km: number;
}

const CnNumberCell: React.FC<{ cnNumber: string }> = ({ cnNumber }) => {
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);

  return (
    <div className="flex items-center space-x-2 w-[120px]">
      <span className="font-medium text-xs">{cnNumber}</span>
      <HeroIcons.DocumentTextIcon
        className="h-4 w-4 text-gray-500 cursor-pointer hover:text-gray-800"
        onClick={() => setIsDocModalOpen(true)}
      />
      <CnDocumentModal
        isOpen={isDocModalOpen}
        onClose={() => setIsDocModalOpen(false)}
        cnNumber={cnNumber}
      />
    </div>
  );
};

export const dmvColumns: ColumnDef<DMVShipment>[] = [
  {
    accessorKey: "cn_number",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="CN Number" />
    ),
    cell: ({ row }) => {
      const cnNumber = row.getValue("cn_number") as string;
      return <CnNumberCell cnNumber={cnNumber} />;
    },
    enableSorting: true,
    enableHiding: false,
  },
  {
    accessorKey: "cn_date",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="CN Date" />
    ),
    cell: ({ row }) => {
      const date = new Date(row.getValue("cn_date"));
      const formatted = date.toLocaleDateString("en-US", {
        month: "short", 
        day: "numeric", 
        year: "numeric", 
      });
      return <div className="w-[100px] text-xs">{formatted}</div>
    },
    enableSorting: true,
  },
  {
    accessorKey: "branch_name",
    header: createStandardHeader("Branch"),
    cell: ({ row }) => {
      const branchName = row.getValue("branch_name") as string
      return (
        <div className="max-w-[120px] truncate text-xs" title={branchName}>
          {titleCase(branchName)}
        </div>
      )
    },
    enableSorting: true,
  },
  {
    accessorKey: "consignor",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Consignor" />
    ),
    cell: ({ row }) => {
      const consignor = row.getValue("consignor") as string
      return (
        <div className="max-w-[150px] truncate text-xs" title={consignor}>
          {upperCase(consignor)}
        </div>
      )
    },
    enableSorting: true,
  },
  {
    accessorKey: "consignee",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Consignee" />
    ),
    cell: ({ row }) => {
      const consignee = row.getValue("consignee") as string
      return (
        <div className="max-w-[150px] truncate text-xs" title={consignee}>
          {upperCase(consignee)}
        </div>
      )
    },
    enableSorting: true,
  },
  {
    accessorKey: "origin",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Origin" />
    ),
    cell: ({ row }) => {
      const origin = row.getValue("origin") as string
      return (
        <div className="max-w-[120px] truncate text-xs" title={origin}>
          {titleCase(origin)}
        </div>
      )
    },
    enableSorting: true,
  },
  {
    accessorKey: "destination",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Destination" />
    ),
    cell: ({ row }) => {
      const destination = row.getValue("destination") as string
      return (
        <div className="max-w-[120px] truncate text-xs" title={destination}>
          {titleCase(destination)}
        </div>
      )
    },
    enableSorting: true,
  },
  {
    accessorKey: "cn_distance_km",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="CN Distance" />
    ),
    cell: ({ row }) => {
      const totalDistance = row.getValue("cn_distance_km");
      return (
        <div className="w-[70px] text-xs">
          {totalDistance ? `${totalDistance} km` : "-"}
        </div>
      );
    },
    enableSorting: true,
  },
]
